import { InfoTooltip } from '@parcel/ui';
import type * as React from 'react';
import type { ApiProject } from '../../lib/projects.js';
import { CitedValue } from '../entity-drawer/field-evidence-link.js';

export type ProjectDataFacts = Pick<
  ApiProject,
  | 'land_area_sf'
  | 'stories'
  | 'height_ft'
  | 'far'
  | 'structure_types'
  | 'residential_units'
  | 'unit_mix'
  | 'affordable_units'
  | 'hotel_rooms'
  | 'parking_spaces'
  | 'cost_of_construction_usd'
  | 'use_areas'
  | 'field_evidence'
>;

interface Row {
  label: string;
  value: React.ReactNode;
  evidenceKey?: string;
  hint?: string;
}

const UNKNOWN = '—';
const FAR_EXPLAINED = 'Building floor area ÷ land area.';

const USE_AREA_LABELS: Readonly<Record<string, string>> = {
  retail: 'Retail',
  office_medical: 'Office / medical',
  industrial_manufacturing: 'Industrial / manufacturing',
  warehouse_shipping: 'Warehouse / shipping',
  research_development: 'Research & development',
  educational_institutional: 'Educational / institutional',
  hotel_room: 'Hotel',
};

const UNIT_MIX_PARTS = [
  ['studio_or_1br', 'Studio/1BR'],
  ['two_br', '2BR'],
  ['three_br_plus', '3BR+'],
] as const;

const AFFORDABLE_BANDS = [
  ['ami_0_30', '0–30% AMI'],
  ['ami_30_50', '30–50% AMI'],
  ['ami_50_80', '50–80% AMI'],
  ['ami_80_120', '80–120% AMI'],
  ['ami_unknown', 'AMI not stated'],
] as const;

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const count = (n: number | null) => (n === null ? UNKNOWN : n.toLocaleString('en-US'));
const squareFeet = (n: number | null) => (n === null ? UNKNOWN : `${count(n)} sq ft`);
const dollars = (n: number | null) =>
  n === null
    ? UNKNOWN
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Known members of a breakdown, each with its own source link. */
function Breakdown({
  parts,
  evidencePrefix,
  facts,
}: {
  parts: ReadonlyArray<readonly [string, string, number | null]>;
  evidencePrefix: string;
  facts: KnownFacts;
}) {
  return (
    <ul className="space-y-0.5">
      {parts
        .filter(([, , n]) => n !== null)
        .map(([key, label, n]) => (
          <li key={key}>
            <CitedValue evidence={facts.field_evidence[`${evidencePrefix}.${key}`]}>
              {`${label} ${count(n)}`}
            </CitedValue>
          </li>
        ))}
    </ul>
  );
}

function buildGroups(facts: KnownFacts): Array<{ title: string; rows: Row[] }> {
  const mix = facts.unit_mix;
  const bands = facts.affordable_units;
  const size: Row[] = [
    { label: 'Land SF', value: squareFeet(facts.land_area_sf), evidenceKey: 'land_area_sf' },
    { label: 'Stories', value: count(facts.stories), evidenceKey: 'stories' },
  ];
  if (facts.height_ft !== null)
    size.push({ label: 'Height', value: `${count(facts.height_ft)} ft`, evidenceKey: 'height_ft' });
  if (facts.far)
    size.push({
      label: 'Floor area ratio',
      hint: FAR_EXPLAINED,
      value: (
        <>
          {facts.far.value.toFixed(2).replace(/\.?0+$/, '')}
          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            Calculated
          </span>
        </>
      ),
    });
  if (facts.structure_types.length > 0)
    size.push({
      label: 'Structure',
      value: (
        <ul className="space-y-0.5">
          {facts.structure_types.map((type) => (
            <li key={type}>
              <CitedValue evidence={facts.field_evidence[`structure_types.${type}`]}>
                {humanize(type)}
              </CitedValue>
            </li>
          ))}
        </ul>
      ),
    });

  const units: Row[] = [
    {
      label: 'Residential units',
      value: count(facts.residential_units),
      evidenceKey: 'residential_units',
    },
  ];
  if (mix)
    units.push({
      label: 'Unit mix',
      value: (
        <Breakdown
          parts={UNIT_MIX_PARTS.map(([key, label]) => [key, label, mix[key]] as const)}
          evidencePrefix="unit_mix"
          facts={facts}
        />
      ),
    });
  if (bands)
    units.push({
      label: 'Affordable units',
      value: (
        <Breakdown
          parts={AFFORDABLE_BANDS.map(([key, label]) => [key, label, bands[key]] as const)}
          evidencePrefix="affordable_units"
          facts={facts}
        />
      ),
    });
  if (facts.hotel_rooms !== null)
    units.push({
      label: 'Hotel rooms',
      value: count(facts.hotel_rooms),
      evidenceKey: 'hotel_rooms',
    });

  return [
    { title: 'Size & space', rows: size },
    { title: 'Units', rows: units },
    {
      title: 'Parking',
      rows: [
        {
          label: 'Parking spaces',
          value: count(facts.parking_spaces),
          evidenceKey: 'parking_spaces',
        },
      ],
    },
    {
      title: 'Cost',
      rows: [
        {
          label: 'Construction cost',
          value: dollars(facts.cost_of_construction_usd),
          evidenceKey: 'cost_of_construction_usd',
        },
      ],
    },
    {
      title: 'Use areas',
      rows: facts.use_areas.map((area) => ({
        label: USE_AREA_LABELS[area.use_type] ?? humanize(area.use_type),
        value: squareFeet(area.floor_area_sf),
        evidenceKey: `use_areas.${area.use_type}`,
      })),
    },
  ].filter((group) => group.rows.length > 0);
}

type ListFact = 'structure_types' | 'use_areas' | 'field_evidence';
type KnownFacts = { [K in ListFact]-?: NonNullable<ProjectDataFacts[K]> } & {
  [K in Exclude<keyof ProjectDataFacts, ListFact>]-?: NonNullable<ProjectDataFacts[K]> | null;
};

// An API that predates a field omits it; read that as unknown, never as a crash.
function withDefaults(facts: ProjectDataFacts): KnownFacts {
  return {
    land_area_sf: facts.land_area_sf ?? null,
    stories: facts.stories ?? null,
    height_ft: facts.height_ft ?? null,
    far: facts.far ?? null,
    structure_types: facts.structure_types ?? [],
    residential_units: facts.residential_units ?? null,
    unit_mix: facts.unit_mix ?? null,
    affordable_units: facts.affordable_units ?? null,
    hotel_rooms: facts.hotel_rooms ?? null,
    parking_spaces: facts.parking_spaces ?? null,
    cost_of_construction_usd: facts.cost_of_construction_usd ?? null,
    use_areas: facts.use_areas ?? [],
    field_evidence: facts.field_evidence ?? {},
  };
}

/** The project's size, units, parking, cost and use areas, each linked to its source. */
export function ProjectDataSection({
  facts,
  columns = 2,
}: {
  facts: ProjectDataFacts;
  columns?: 1 | 2;
}) {
  const known = withDefaults(facts);
  return (
    <div className="space-y-6 text-sm">
      {buildGroups(known).map((group) => (
        <section key={group.title} data-group={group.title} className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
          <dl className={`grid gap-x-8 gap-y-2.5 ${columns === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {group.rows.map((row) => (
              <div
                key={row.label}
                data-field={row.label}
                className="grid min-w-0 grid-cols-[7.5rem_minmax(0,1fr)] items-baseline gap-3"
              >
                <dt className="text-muted-foreground">
                  {row.hint ? <InfoTooltip content={row.hint}>{row.label}</InfoTooltip> : row.label}
                </dt>
                <dd className="min-w-0 break-words text-foreground">
                  {row.evidenceKey ? (
                    <CitedValue evidence={known.field_evidence[row.evidenceKey]}>
                      {row.value}
                    </CitedValue>
                  ) : (
                    row.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
